import { ArrowRight } from "lucide-react";
import { Footer } from "@/components/Footer";
import { SmartLink } from "@/components/Header";
import { PageMeta } from "@/components/site/blocks";
import { Marquee } from "@/components/site/showcase";
import { AppDock } from "@/components/site/atl";
import { HeroStage } from "@/components/home/HeroStage";
import { ProductTour } from "@/components/home/ProductTour";
import { UseCases } from "@/components/home/UseCases";
import { DayOne } from "@/components/home/DayOne";
import { ToolsWall } from "@/components/home/ToolsWall";
import { Rise } from "@/components/home/motion";
import { Rules } from "@/components/home/Rules";
import { liveProducts } from "@/data/products";
import { GET_STARTED_URL, REQUEST_DEMO_URL } from "@/data/site";

// The homepage is about the company. Its sections, top to bottom (2026-09-28, after
// Attio and Clay): the hero with the workspace rising as you scroll and the app dock,
// the three products in a sticky list, use cases that move on by themselves, the tools
// wall, "live from day one", how we build, and the closing call. Products come from
// data/products.ts; the pictures live in components/home/.

const facts = ["AI products for real work", "Connect the apps you use", "Credentials encrypted at rest", "Revoke access in one click", "You approve what AI does", "One login for every product", "Free to start"];

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://clearmakes.com/#organization",
      name: "ClearMakes",
      url: "https://clearmakes.com/",
      logo: { "@type": "ImageObject", url: "https://clearmakes.com/logo.png" },
      description: "ClearMakes makes intelligent software for the way teams actually work: people decide, AI agents do the rest. Less coordination, more finished work.",
      email: "contact@clearmakes.com",
      contactPoint: { "@type": "ContactPoint", contactType: "customer support", email: "support@clearmakes.com", availableLanguage: "English" },
      // Official profiles only. Add LinkedIn, X, Product Hunt, G2 and the rest once they are
      // claimed (gtm/site/brand.md lists them).
      sameAs: ["https://github.com/ClearMakes", "https://www.linkedin.com/company/clearmakes", "https://www.youtube.com/@clearmakes"],
      owns: liveProducts.map((p) => ({ "@type": "SoftwareApplication", name: p.name, url: p.url ?? `https://clearmakes.com/products/${p.slug}` })),
    },
    { "@type": "WebSite", "@id": "https://clearmakes.com/#website", url: "https://clearmakes.com/", name: "ClearMakes", publisher: { "@id": "https://clearmakes.com/#organization" } },
    {
      "@type": "ItemList",
      name: "ClearMakes products",
      itemListElement: liveProducts.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.name, url: p.url ?? `https://clearmakes.com/products/${p.slug}` })),
    },
  ],
};

const Index = () => (
  <>
    <PageMeta
      title="ClearMakes: AI Workflows, AI Agents and Meeting Assistant"
      tabTitle="ClearMakes"
      description="ClearMakes makes three AI tools for teams: Inbox runs workflows on new email and chat, Agents answer from your documents, and ClearMeet helps in meetings."
      path="/"
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

    <main className="overflow-x-clip bg-background text-foreground">
      {/* Hero: the workspace rises as you scroll; the app dock rides the bottom of the
          screen until this section ends */}
      {/* -mt pulls the dark hero up behind the floating header card */}
      <section aria-labelledby="hero-h" className="relative -mt-[106px] bg-[hsl(0_0%_4%)] pb-10 pt-[150px] text-[hsl(0_0%_100%)] sm:pt-[172px]">
        <div aria-hidden className="cm-grid-dark pointer-events-none absolute inset-x-0 top-0 h-[720px]" />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 top-[30%] bg-[radial-gradient(75%_60%_at_50%_85%,hsl(216_84%_52%/0.55),transparent_70%)]" />
        <div className="relative mx-auto max-w-[1180px] px-5 text-center sm:px-8">
          <Rise y={16}>
            <h1 id="hero-h" className="mx-auto max-w-6xl text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.045em] sm:text-[4rem] lg:text-[4.6rem]">
              Clear work for teams and agents.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-[1.15rem] leading-snug text-[hsl(0_0%_100%/0.7)] sm:text-[1.35rem]">
              People decide. AI agents do the rest.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <SmartLink href={REQUEST_DEMO_URL} className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-[hsl(0_0%_100%/0.25)] px-6 text-[16px] font-medium text-[hsl(0_0%_100%)] transition-colors hover:bg-[hsl(0_0%_100%/0.08)] sm:w-auto">
                Talk to us
              </SmartLink>
              <SmartLink href={GET_STARTED_URL} className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[hsl(0_0%_100%)] px-6 text-[16px] font-semibold text-[hsl(0_0%_4%)] transition-colors hover:bg-[hsl(216_84%_52%)] hover:text-[hsl(0_0%_100%)] sm:w-auto">
                Get started free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </SmartLink>
            </div>
          </Rise>
        </div>
        <div className="relative mt-12 px-3 sm:mt-14 sm:px-6">
          <HeroStage />
        </div>
        <AppDock />
      </section>

      {/* The three products, one at a time */}
      <ProductTour />

      {/* Use cases that move on by themselves */}
      <UseCases />

      {/* Start with one, then the tools wall */}
      <ToolsWall />

      {/* Live from day one */}
      <DayOne />

      {/* How we build: four rules, one picture per rule */}
      <section aria-labelledby="beliefs-h" className="bg-[linear-gradient(180deg,hsl(216_84%_50%),hsl(218_80%_40%))] py-24 text-[hsl(0_0%_100%)] sm:py-32">
        <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
          <Rules />
        </div>
        <div className="mt-24">
          <Marquee speed={46}>
            {facts.map((f) => (
              <span key={f} className="flex shrink-0 items-center gap-12 text-[1.6rem] font-semibold tracking-[-0.025em] text-[hsl(0_0%_100%)] sm:text-[2rem]">
                {f} <span className="h-2 w-2 rounded-full bg-[hsl(0_0%_100%/0.7)]" />
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Closing */}
      <section aria-labelledby="cta-h" className="relative bg-[hsl(0_0%_4%)] text-[hsl(0_0%_100%)]">
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_70%_at_50%_0%,hsl(216_84%_52%/0.4),transparent_70%)]" />
        <div className="relative mx-auto max-w-[1180px] px-5 py-28 text-center sm:px-8 sm:py-36">
          <Rise>
            <h2 id="cta-h" className="mx-auto max-w-4xl text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.045em] sm:text-[4.6rem]">
              Spend your day on the work that <span className="text-[hsl(216_90%_66%)]">needs you</span>.
            </h2>
            <p className="mx-auto mt-7 max-w-xl text-[19px] leading-relaxed text-[hsl(0_0%_100%/0.7)]">One free ClearMakes workspace opens every product we make.</p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <SmartLink href={GET_STARTED_URL} className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[hsl(0_0%_100%)] px-6 text-[16px] font-semibold text-[hsl(0_0%_4%)] transition-colors hover:bg-[hsl(216_84%_52%)] hover:text-[hsl(0_0%_100%)] sm:w-auto">Get started free</SmartLink>
              <SmartLink href="/products" className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-[hsl(0_0%_100%/0.25)] px-6 text-[16px] font-medium text-[hsl(0_0%_100%)] transition-colors hover:bg-[hsl(0_0%_100%/0.08)] sm:w-auto">Explore products</SmartLink>
            </div>
          </Rise>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default Index;
